import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CheckCircle, AlertCircle, Upload, Save } from 'lucide-react';
import { heroAPI, uploadAPI } from '../../utils/api';
import { toast } from 'sonner';

const AdminContent = () => {
  const [heroContent, setHeroContent] = useState({
    title: '',
    subtitle: '',
    description: '',
    video_url: '',
    cta_button_text: '',
    bot_link: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoFile, setVideoFile] = useState(null);

  useEffect(() => {
    fetchHeroContent();
  }, []);

  const fetchHeroContent = async () => {
    try {
      const response = await heroAPI.get();
      setHeroContent(response.data);
    } catch (error) {
      console.error('Error fetching hero content:', error);
      toast.error('Ошибка загрузки контента');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setHeroContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Пожалуйста, выберите видео файл');
      return;
    }

    setUploading(true);
    try {
      const response = await uploadAPI.uploadFile(file);
      const videoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      setHeroContent(prev => ({
        ...prev,
        video_url: videoUrl
      }));
      
      toast.success('Видео успешно загружено');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error('Ошибка загрузки видео');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await heroAPI.update(heroContent);
      toast.success('Контент успешно сохранён');
    } catch (error) {
      console.error('Error saving hero content:', error);
      toast.error('Ошибка сохранения контента');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Управление контентом</h1>
          <p className="text-gray-400">Редактирование контента главной страницы</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="save-content-btn"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? 'Сохранение...' : 'Сохранить изменения'}
        </Button>
      </div>

      <Tabs defaultValue="hero" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-gray-800">
          <TabsTrigger value="hero" className="data-[state=active]:bg-blue-600">
            Главная секция
          </TabsTrigger>
          <TabsTrigger value="video" className="data-[state=active]:bg-blue-600">
            Видео
          </TabsTrigger>
          <TabsTrigger value="preview" className="data-[state=active]:bg-blue-600">
            Предварительный просмотр
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hero" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Основной контент</CardTitle>
              <CardDescription className="text-gray-400">
                Настройте заголовки и описания для главной страницы
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-gray-300">
                    Основной заголовок
                  </Label>
                  <Input
                    id="title"
                    value={heroContent.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="AI-переводчик текста и голоса"
                    data-testid="hero-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subtitle" className="text-gray-300">
                    Подзаголовок
                  </Label>
                  <Input
                    id="subtitle"
                    value={heroContent.subtitle}
                    onChange={(e) => handleInputChange('subtitle', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Перевожу в реальном времени между людьми в чате"
                    data-testid="hero-subtitle-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-gray-300">
                    Описание
                  </Label>
                  <Textarea
                    id="description"
                    value={heroContent.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
                    placeholder="Получите 15 бесплатных сообщений и начните переводить уже сейчас!"
                    data-testid="hero-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cta_button" className="text-gray-300">
                      Текст кнопки
                    </Label>
                    <Input
                      id="cta_button"
                      value={heroContent.cta_button_text}
                      onChange={(e) => handleInputChange('cta_button_text', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="Начать переводить бесплатно"
                      data-testid="hero-cta-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bot_link" className="text-gray-300">
                      Ссылка на бота
                    </Label>
                    <Input
                      id="bot_link"
                      value={heroContent.bot_link}
                      onChange={(e) => handleInputChange('bot_link', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="https://t.me/lngaua_bot"
                      data-testid="hero-bot-link-input"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="video" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Управление видео</CardTitle>
              <CardDescription className="text-gray-400">
                Загрузите видео для отображения на главной странице
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current video */}
              {heroContent.video_url && (
                <div className="space-y-4">
                  <Label className="text-gray-300">Текущее видео</Label>
                  <div className="aspect-video max-w-md rounded-lg overflow-hidden bg-gray-900">
                    <video 
                      controls 
                      className="w-full h-full object-cover"
                      src={heroContent.video_url}
                    >
                      Ваш браузер не поддерживает видео.
                    </video>
                  </div>
                  <div className="text-sm text-gray-400">
                    URL: {heroContent.video_url}
                  </div>
                </div>
              )}

              {/* Upload new video */}
              <div className="space-y-4">
                <Label className="text-gray-300">Загрузить новое видео</Label>
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
                  <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="text-gray-300 mb-2">
                    Нажмите для выбора видео файла
                  </div>
                  <div className="text-sm text-gray-400 mb-4">
                    Поддерживаются форматы: MP4, WebM, MOV
                  </div>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                    id="video-upload"
                    disabled={uploading}
                  />
                  <Button
                    onClick={() => document.getElementById('video-upload').click()}
                    disabled={uploading}
                    variant="outline"
                    className="border-gray-600 text-gray-300"
                  >
                    {uploading ? 'Загружается...' : 'Выбрать файл'}
                  </Button>
                </div>
              </div>

              {/* Manual URL input */}
              <div className="space-y-2">
                <Label htmlFor="video_url" className="text-gray-300">
                  Или введите URL видео вручную
                </Label>
                <Input
                  id="video_url"
                  value={heroContent.video_url}
                  onChange={(e) => handleInputChange('video_url', e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="https://example.com/video.mp4"
                  data-testid="hero-video-url-input"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Предварительный просмотр</CardTitle>
              <CardDescription className="text-gray-400">
                Как будет выглядеть главная секция на сайте
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Preview */}
              <div className="bg-gray-900 rounded-lg p-8 min-h-[400px] flex items-center">
                <div className="grid lg:grid-cols-2 gap-8 items-center w-full">
                  <div className="text-center lg:text-left">
                    <h1 className="text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                      {heroContent.title || 'Заголовок'}
                    </h1>
                    <p className="text-lg text-gray-300 mb-3">
                      {heroContent.subtitle || 'Подзаголовок'}
                    </p>
                    <p className="text-gray-400 mb-6">
                      {heroContent.description || 'Описание'}
                    </p>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                      <Button className="bg-blue-600 hover:bg-blue-700 text-sm">
                        {heroContent.cta_button_text || 'Текст кнопки'}
                      </Button>
                      <Button variant="outline" className="border-gray-600 text-gray-300 text-sm">
                        Смотреть демо
                      </Button>
                    </div>
                  </div>
                  
                  <div className="relative">
                    {heroContent.video_url ? (
                      <div className="aspect-video rounded-xl overflow-hidden shadow-lg">
                        <video 
                          controls 
                          className="w-full h-full object-cover"
                          src={heroContent.video_url}
                        >
                          Ваш браузер не поддерживает видео.
                        </video>
                      </div>
                    ) : (
                      <div className="aspect-video rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                        <div className="text-center text-white">
                          <Upload className="h-12 w-12 mx-auto mb-2 opacity-60" />
                          <p className="opacity-60">Видео не загружено</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save reminder */}
      <Alert className="border-yellow-600 bg-yellow-600/10">
        <AlertCircle className="h-4 w-4 text-yellow-400" />
        <AlertDescription className="text-yellow-400">
          Не забудьте сохранить изменения после редактирования контента
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default AdminContent;